import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  UnorderedList,
  ListItem,
  OrderedList,
  Divider,
  Icon,
  Alert,
  AlertIcon,
  Badge,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Flex
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  FaFileContract,
  FaGem,
  FaCreditCard,
  FaShippingFast,
  FaExchangeAlt,
  FaMoneyBillWave,
  FaDollarSign,
  FaShieldAlt,
  FaCogs,
  FaEdit,
  FaEnvelope,
  FaClock,
  FaTruck,
  FaUndo
} from 'react-icons/fa';

// Add Google Fonts for luxury typography
const fontImport = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
`;

// Inject the font styles
if (typeof document !== 'undefined' && !document.head.querySelector('[data-terms-fonts]')) {
  const styleElement = document.createElement('style');
  styleElement.textContent = fontImport;
  styleElement.setAttribute('data-terms-fonts', 'true');
  document.head.appendChild(styleElement);
}

const MotionBox = motion(Box);

const TermsConditionsPage = () => {
  const [expandedSections, setExpandedSections] = useState(new Set([0])); // First section expanded by default

  const quickOverview = [
    {
      icon: FaGem,
      title: 'Premium Quality',
      description: 'High-grade stainless steel jewelry with quality checks'
    },
    {
      icon: FaCreditCard,
      title: 'Secure Payments',
      description: 'Razorpay gateway with multiple payment options'
    },
    {
      icon: FaShippingFast,
      title: 'Fast Shipping',
      description: '1-3 days processing, 3-7 days delivery nationwide'
    },
    {
      icon: FaUndo,
      title: '48hr Return Policy',
      description: 'Returns accepted for damaged items with proof'
    }
  ];

  const sections = [
    {
      id: 1,
      icon: FaGem,
      title: 'Product Information & Quality',
      color: 'purple.500',
      content: [
        'At AGNIR, we pride ourselves on craftsmanship. Our jewelry is made from high-grade stainless steel and skin-safe alloys.',
        'We aim to present each product with accurate descriptions and true-to-life images. However, minor variations in color or texture may occur due to differences in lighting, device screens, or the handmade nature of the product.',
        'We conduct multiple quality checks before dispatching each order to ensure that you receive premium quality.'
      ]
    },
    {
      id: 2,
      icon: FaCreditCard,
      title: 'Orders & Payments',
      color: 'blue.500',
      content: [
        'All orders placed on our website are processed through Razorpay, a secure and reliable payment gateway that supports UPI, credit/debit cards, wallets, and net banking.',
        'Once your payment is successfully processed, you will receive a confirmation email with your order details.',
        'Currently, we do not accept Cash on Delivery (COD). In case your transaction fails or is flagged, we will not process your order until the issue is resolved.'
      ]
    },
    {
      id: 3,
      icon: FaClock,
      title: 'Order Confirmation & Tracking',
      color: 'green.500',
      content: [
        'Once your order is placed and confirmed, you will receive an email with your order number and summary.',
        'After dispatch, you\'ll receive a tracking link via email or WhatsApp, which you can use to track your package in real-time.',
        'If you haven\'t received tracking details within 3 business days, please reach out to us.'
      ]
    },
    {
      id: 4,
      icon: FaTruck,
      title: 'Shipping & Delivery',
      color: 'orange.500',
      content: [
        'We offer nationwide shipping across India using trusted courier partners.'
      ],
      details: {
        'Processing Time': '1-3 business days',
        'Delivery Time': '3-7 business days depending on your location'
      },
      note: 'Shipping delays may occur during festivals, holidays, or due to unforeseen events. AGNIR is not responsible for such delays or for issues arising from incorrect delivery addresses provided by customers.'
    },
    {
      id: 5,
      icon: FaExchangeAlt,
      title: 'Returns, Exchange & Cancellation Policy',
      color: 'red.500',
      subsections: [
        {
          title: 'Return Eligibility',
          content: [
            'Due to hygiene and product handling, we do not accept returns or refunds unless the product arrives damaged or defective.',
            'You must notify us within 48 hours of receiving a damaged item with clear unboxing proof (photo/video).',
            'We do not accept returns for reasons such as change of mind or not liking the design.',
            'For sizing issues on rings or bracelets, exchanges may be available.'
          ]
        },
        {
          title: 'Cancellations',
          content: [
            'Orders can be cancelled within 2 hours of placing the order. After this, the item is processed and prepared for shipping, and cancellation won\'t be possible.'
          ]
        }
      ]
    },
    {
      id: 6,
      icon: FaMoneyBillWave,
      title: 'Refund Policy',
      color: 'teal.500',
      content: [
        'Refunds are issued only after we receive and inspect the returned item.',
        'If the item is approved (unused, undamaged, original packaging intact), the refund will be initiated within 5-7 working days to the original payment method.',
        'If the return does not meet the conditions, the product will be returned back to the customer without refund.',
        'Shipping and payment gateway fees are non-refundable.'
      ]
    },
    {
      id: 7,
      icon: FaDollarSign,
      title: 'Modification to Pricing & Services',
      color: 'yellow.500',
      content: [
        'We may update product prices, features, and services at any time without prior notice.',
        'Any promotional offers or discounts may be discontinued or altered as per our discretion. Prices displayed at the time of checkout are final and binding.'
      ]
    },
    {
      id: 8,
      icon: FaShieldAlt,
      title: 'Responsibility & Liability',
      color: 'indigo.500',
      content: [
        'We ensure that our products are made from quality materials. However:',
        'AGNIR is not liable for allergic reactions or skin sensitivities caused by individual conditions.',
        'We are not responsible for loss, damage, or misuse of the product after successful delivery.',
        'Customers are expected to follow the product care instructions provided with each order.'
      ]
    },
    {
      id: 9,
      icon: FaCogs,
      title: 'Third-Party Services',
      color: 'cyan.500',
      content: [
        'We work with third-party partners for:'
      ],
      services: [
        'Payment processing (Razorpay)',
        'Courier and logistics services via Bigship or Shiprocket',
        'Analytics to improve our website'
      ],
      note: 'These services only access your data to the extent required and are under strict confidentiality agreements.'
    },
    {
      id: 10,
      icon: FaEdit,
      title: 'Changes to Terms',
      color: 'pink.500',
      content: [
        'AGNIR reserves the right to modify these Terms & Conditions at any time. When changes are made, they are effective immediately upon posting. Continued use of our site means you agree to the updated terms.'
      ]
    }
  ];

  return (
    <Box 
      minH="100vh" 
      bgImage={`url(${process.env.REACT_APP_BG_URL})`}
      py={{ base: 10, lg: 20 }}
    >
      <Container maxW="container.xl">
        {/* Header Section */}
        <MotionBox
          textAlign="center"
          mb={{ base: 12, lg: 16 }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <HStack justify="center" mb={6}>
            <Icon 
              as={FaFileContract} 
              boxSize={{ base: 8, lg: 10 }} 
              color="gray.600" 
            />
          </HStack>
          <Heading
            as="h1"
            size={{ base: "2xl", lg: "3xl" }}
            fontFamily="'Playfair Display', serif"
            fontWeight="700"
            color="gray.800"
            mb={6}
            lineHeight="1.2"
          >
            Terms & Conditions
          </Heading>
          <Text
            fontSize={{ base: "lg", lg: "xl" }}
            color="gray.600"
            fontFamily="'Inter', sans-serif"
            maxW="800px"
            mx="auto"
            lineHeight="1.7"
            mb={6}
          >
            This website is owned and operated by AGNIR. Throughout the site, the terms "we", "us", and "our" refer to AGNIR. 
            By using our site and purchasing from us, you engage in our Services and agree to be bound by the following Terms & Conditions.
          </Text>
          
          <Alert status="info" borderRadius="xl" maxW="700px" mx="auto" bg="blue.50" border="1px solid" borderColor="blue.200">
            <AlertIcon color="blue.500" />
            <Text fontSize="md" fontFamily="'Inter', sans-serif" color="blue.800">
              We may change or update any section of these Terms & Conditions at any time without prior notice. 
              We encourage you to revisit this page regularly to stay informed about any changes.
            </Text>
          </Alert>
        </MotionBox>

        {/* Quick Overview */}
        <MotionBox
          mb={{ base: 12, lg: 16 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Heading
            as="h2"
            size="xl"
            fontFamily="'Playfair Display', serif"
            fontWeight="600"
            color="gray.800"
            textAlign="center"
            mb={8}
          >
            Quick Overview
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            {quickOverview.map((item, index) => (
              <MotionBox
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <VStack
                  spacing={4}
                  p={6}
                  bg="white"
                  borderRadius="xl"
                  boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
                  border="1px solid"
                  borderColor="gray.200"
                  textAlign="center"
                  minH="180px"
                  justify="center"
                >
                  <Box
                    p={3}
                    borderRadius="full"
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Icon as={item.icon} boxSize={6} color="gray.600" />
                  </Box>
                  <Heading
                    as="h3"
                    size="md"
                    fontFamily="'Playfair Display', serif"
                    fontWeight="600"
                    color="gray.800"
                  >
                    {item.title}
                  </Heading>
                  <Text
                    fontSize="sm"
                    color="gray.600"
                    fontFamily="'Inter', sans-serif"
                    lineHeight="1.5"
                  >
                    {item.description}
                  </Text>
                </VStack>
              </MotionBox>
            ))}
          </SimpleGrid>
        </MotionBox>

        {/* Detailed Terms Sections */}
        <MotionBox
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <Heading
            as="h2"
            size="xl"
            fontFamily="'Playfair Display', serif"
            fontWeight="600"
            color="gray.800"
            textAlign="center"
            mb={8}
          >
            Detailed Terms
          </Heading>

          <Accordion allowMultiple defaultIndex={[0]}>
            {sections.map((section, index) => (
              <AccordionItem
                key={section.id}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                mb={4}
                overflow="hidden"
              >
                <AccordionButton
                  p={{ base: 6, lg: 8 }}
                  _hover={{ bg: "gray.50" }}
                  _expanded={{ 
                    bg: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                    borderBottomColor: "gray.300"
                  }}
                  borderBottom="1px solid"
                  borderBottomColor="transparent"
                >
                  <HStack flex="1" spacing={4} align="center">
                    <Box
                      p={2}
                      borderRadius="lg"
                      bg={`${section.color.split('.')[0]}.50`}
                      border="1px solid"
                      borderColor={`${section.color.split('.')[0]}.200`}
                    >
                      <Icon as={section.icon} boxSize={5} color={section.color} />
                    </Box>
                    <VStack align="start" spacing={1} flex={1}>
                      <Heading
                        as="h3"
                        size="md"
                        fontFamily="'Playfair Display', serif"
                        fontWeight="600"
                        color="gray.800"
                        textAlign="left"
                      >
                        {section.id}. {section.title}
                      </Heading>
                    </VStack>
                  </HStack>
                  <AccordionIcon fontSize="24px" color="gray.600" />
                </AccordionButton>

                <AccordionPanel pb={8}>
                  <VStack spacing={6} align="start">
                    {/* Main content */}
                    {section.content && (
                      <VStack spacing={4} align="start" w="full">
                        {section.content.map((paragraph, pIndex) => (
                          <Text
                            key={pIndex}
                            fontSize={{ base: "md", lg: "lg" }}
                            fontFamily="'Inter', sans-serif"
                            color="gray.700"
                            lineHeight="1.7"
                          >
                            {paragraph}
                          </Text>
                        ))}
                      </VStack>
                    )}

                    {/* Details section */}
                    {section.details && (
                      <Box w="full">
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                          {Object.entries(section.details).map(([key, value]) => (
                            <Flex key={key} justify="space-between" align="center" p={4} bg="gray.50" borderRadius="lg">
                              <Text fontFamily="'Inter', sans-serif" fontWeight="600" color="gray.800">
                                {key}:
                              </Text>
                              <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>
                                {value}
                              </Badge>
                            </Flex>
                          ))}
                        </SimpleGrid>
                      </Box>
                    )}

                    {/* Services list */}
                    {section.services && (
                      <Box w="full">
                        <UnorderedList spacing={2} pl={4}>
                          {section.services.map((service, sIndex) => (
                            <ListItem key={sIndex}>
                              <Text
                                fontSize="md"
                                fontFamily="'Inter', sans-serif"
                                color="gray.700"
                                lineHeight="1.6"
                              >
                                {service}
                              </Text>
                            </ListItem>
                          ))}
                        </UnorderedList>
                      </Box>
                    )}

                    {/* Subsections */}
                    {section.subsections && (
                      <VStack spacing={6} align="start" w="full">
                        {section.subsections.map((subsection, subIndex) => (
                          <Box key={subIndex} w="full">
                            <Heading
                              as="h4"
                              size="md"
                              fontFamily="'Playfair Display', serif"
                              fontWeight="600"
                              color="gray.800"
                              mb={3}
                            >
                              {subsection.title}:
                            </Heading>
                            <VStack spacing={3} align="start" pl={4}>
                              {subsection.content.map((item, itemIndex) => (
                                <HStack key={itemIndex} spacing={3} align="start">
                                  <Box
                                    w={2}
                                    h={2}
                                    borderRadius="full"
                                    bg={section.color}
                                    flexShrink={0}
                                    mt={2}
                                  />
                                  <Text
                                    fontSize="md"
                                    fontFamily="'Inter', sans-serif"
                                    color="gray.700"
                                    lineHeight="1.6"
                                  >
                                    {item}
                                  </Text>
                                </HStack>
                              ))}
                            </VStack>
                          </Box>
                        ))}
                      </VStack>
                    )}

                    {/* Section note */}
                    {section.note && (
                      <Alert status="warning" borderRadius="lg" bg="orange.50" border="1px solid" borderColor="orange.200">
                        <AlertIcon color="orange.500" />
                        <Text
                          fontSize="md"
                          fontFamily="'Inter', sans-serif"
                          color="orange.800"
                        >
                          {section.note}
                        </Text>
                      </Alert>
                    )}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </MotionBox>

        {/* Contact Section */}
        <MotionBox
          mt={{ base: 12, lg: 16 }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <Box
            bg="linear-gradient(135deg, #1a202c 0%, #2d3748 100%)"
            borderRadius="2xl"
            p={{ base: 8, lg: 12 }}
            color="white"
            textAlign="center"
          >
            <VStack spacing={6}>
              <Icon as={FaEnvelope} boxSize={10} color="blue.300" />
              <Heading
                as="h3"
                size="xl"
                fontFamily="'Playfair Display', serif"
                fontWeight="600"
              >
                Contact Us
              </Heading>
              <Text
                fontSize={{ base: "lg", lg: "xl" }}
                fontFamily="'Inter', sans-serif"
                lineHeight="1.6"
                maxW="600px"
                opacity="0.9"
              >
                Got questions? Hit us up at our email or DM us on Instagram @agnir.co.
                We typically respond within 24-48 hours during business days.
              </Text>
              <VStack spacing={3}>
                <HStack spacing={6} flexWrap="wrap" justify="center">
                  <Badge colorScheme="blue" fontSize="lg" px={4} py={2} borderRadius="full">
                    agnir.info@gmail.com
                  </Badge>
                  <Badge colorScheme="blue" fontSize="lg" px={4} py={2} borderRadius="full">
                    @agnir.co
                  </Badge>
                  <Badge colorScheme="blue" fontSize="lg" px={4} py={2} borderRadius="full">
                    +91 73876 57127
                  </Badge>
                </HStack>
              </VStack>
            </VStack>
          </Box>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default TermsConditionsPage;